import React, { useRef, useState, useLayoutEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import type { SearchResult } from "../types";

type Props = {
  searchInput: string;
  searchResult: SearchResult[];
  noSearchResult: boolean;
  isSearchResultOpen: boolean;
  onSearch: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onSelectSearchResult: (ev: React.MouseEvent, res: SearchResult) => void;
  onFocus: () => void;
  onBlur: () => void;
};

const SearchInput: React.FC<Props> = ({
  searchInput,
  searchResult,
  noSearchResult,
  isSearchResultOpen,
  onSearch,
  onSelectSearchResult,
  onFocus,
  onBlur,
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const [focused, setFocused] = useState(false);

  const [menuStyle, setMenuStyle] = useState<React.CSSProperties | null>(null);

  const computeMenuPos = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    setMenuStyle({
      position: "absolute",
      top: r.bottom + window.scrollY + 8,
      left: r.left + window.scrollX,
      width: r.width,
      zIndex: 9999,
    });
  }, []);

  useLayoutEffect(() => {
    if (!focused) return;
    computeMenuPos();
    const handler = () => computeMenuPos();
    window.addEventListener("resize", handler);
    window.addEventListener("scroll", handler);
    return () => {
      window.removeEventListener("resize", handler);
      window.removeEventListener("scroll", handler);
    };
  }, [focused, computeMenuPos]);

  const handleFocus = () => {
    setFocused(true);
    onFocus();
  };

  const handleBlur = () => {
    setTimeout(() => {
      setFocused(false);
      onBlur();
    }, 120);
  };

  return (
    <>
      <div
        ref={wrapperRef}
        className={`${focused ? "relative z-[70]" : "relative"} w-full min-w-0`}
      >
        <input
          ref={inputRef}
          value={searchInput}
          onChange={onSearch}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder="Buscar por nombre o servicio"
          className={[
            "w-full h-[44px] px-3 rounded-xl border bg-white",
            "border-gray-300 transition-shadow",
            focused ? "ring-2 ring-[#ce0047]" : "focus:ring-2 focus:ring-[#ce0047]",
          ].join(" ")}
        />
      </div>

      {isSearchResultOpen &&
        menuStyle &&
        createPortal(
          <div
            style={menuStyle}
            className="rounded-xl border border-gray-200 bg-white shadow-xl"
            onMouseDown={(e) => e.preventDefault()}
          >
            <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
              {noSearchResult ? (
                <div className="px-4 py-3 text-sm text-gray-500">
                  Sin resultados
                </div>
              ) : (
                <ul className="py-2">
                  {searchResult.map((res, idx) => {
                    const label =
                      res.type === "Feature"
                        ? String(
                            res.properties?.["Nombre"] ||
                              res.properties?.name ||
                              "Sin nombre"
                          )
                        : res.name;
                    return (
                      <li
                        key={idx}
                        className="px-4 py-3 hover:bg-gray-100 cursor-pointer flex items-start gap-2"
                        onMouseDown={(e) => e.preventDefault()}
                        onClick={(e) => onSelectSearchResult(e, res)}
                      >
                        <span className="material-icons-outlined text-gray-600 mr-2">
                          {res.type === "Feature" ? "place" : "search"}
                        </span>
                        <span className="text-sm leading-snug">{label}</span>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
};

export default SearchInput;
